import { DateTime, Duration } from "luxon";

import type { ServiceId } from "@core/domains/identity";
import type { Promisable } from "@core/foundation/promise";
import type { Stage } from "@core/foundation/stage";
import type { JwtSignerContract } from "@core/ports/signing";

import { ServiceClaimsSchema } from "../../models/index.ts";

const SERVICE_TOKEN_LIFETIME = Duration.fromObject({ hours: 1 });

const REFRESH_MARGIN = Duration.fromObject({ minutes: 5 });

interface CacheEntry {
  readonly expiresAt: number;
  readonly tokenPromise: Promisable<string>;
}

export class ServiceJwtSigner {
  // @mutable
  private cache: CacheEntry | undefined;

  private readonly serviceId: ServiceId;

  private readonly signer: JwtSignerContract;

  private readonly stage: Stage;

  public constructor({
    serviceId,
    signer,
    stage,
  }: {
    readonly serviceId: ServiceId;
    readonly signer: JwtSignerContract;
    readonly stage: Stage;
  }) {
    this.serviceId = serviceId;
    this.signer = signer;
    this.stage = stage;
  }

  public async sign(): Promise<string> {
    const utcNow = DateTime.utc();

    const now = Math.floor(utcNow.toSeconds());

    const cached = this.cache;

    if (cached && now < cached.expiresAt - REFRESH_MARGIN.as("seconds")) {
      return cached.tokenPromise;
    }

    const exp = Math.floor(utcNow.plus(SERVICE_TOKEN_LIFETIME).toSeconds());

    const payload = ServiceClaimsSchema.decode({
      exp,
      iat: now,
      service: this.serviceId,
      stage: this.stage,
      type: "service",
      version: 1,
    });

    const tokenPromise = (async () => {
      try {
        return await this.signer.sign(payload);
      } catch (error) {
        this.cache = undefined;

        throw error;
      }
    })();

    this.cache = { expiresAt: exp, tokenPromise };

    return tokenPromise;
  }
}
